import { ButtonGradientPrimary, Card, Colors, Icon, PageContainer } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PillarsSection from './PillarsSection';
import StrategicVisionSection from './StrategicVisionSection';
import ValueCard from './ValueCard';
import { VISION_SECTION_TITLE } from './visionValuesStyles';
import AddEditValueModal from '../AddEditValueModal/AddEditValueModal';
import OverlayLoader from '../OverlayLoader/OverlayLoader';

import type { CompanyValue } from '@/types/company-values';

import {
    useGetCompanyValues,
    useCreateCompanyValue,
    useUpdateCompanyValue,
} from '@/api/company-values';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

// The prototype's breathing room between the page's three sections, in real pixels (48 px on a
// phone, 84 px from `md`): each section reads as its own step instead of one dense column.
const SECTION_STACK = 'mx-3 mt-8 flex flex-col max-md:gap-[48px] md:gap-[84px]';

export default function VisionValuesPage() {
    const { t } = useTranslation();
    const { handleError } = useError();
    const { data: companyValues = [], isLoading, error } = useGetCompanyValues();
    const createCompanyValue = useCreateCompanyValue();
    const updateCompanyValue = useUpdateCompanyValue();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingValue, setEditingValue] = useState<CompanyValue | undefined>();

    const maxValuesReached = companyValues.length >= 8;

    const handleAddValue = () => {
        setEditingValue(undefined);
        setIsModalOpen(true);
    };

    const handleEditValue = (value: CompanyValue) => {
        setEditingValue(value);
        setIsModalOpen(true);
    };

    const handleSaveValue = async (
        data: Omit<CompanyValue, 'id' | 'createdAt' | 'updatedAt' | 'companyId' | 'sortOrder'>,
    ) => {
        try {
            if (editingValue) {
                await updateCompanyValue.mutateAsync({ id: editingValue.id, request: data });
            } else {
                await createCompanyValue.mutateAsync(data);
            }
            setIsModalOpen(false);
            showSuccessToast({ message: t('visionValues.saveSuccess') });
        } catch (error) {
            handleError({ error });
            throw new Error('Save failed');
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingValue(undefined);
    };

    // The values card owns its own loading and error states so a slow or failing
    // company-values call never hides the strategic vision and pillars sections.
    // Block order: company values, then strategic vision, then strategic pillars.
    if (isLoading || error) {
        return (
            <PageContainer>
                <div className={SECTION_STACK}>
                    <Card
                        padding={'xl'}
                        borderRadius={'2xl'}
                        dataTestId={'company-values-section'}
                    >
                        {isLoading ? (
                            <div className={'flex items-center justify-center p-6'}>
                                <OverlayLoader isLoading={isLoading} />
                            </div>
                        ) : (
                            <div
                                role={'alert'}
                                className={'p-6 text-red-500'}
                            >
                                {t('visionValues.errorLoadingValues')}
                            </div>
                        )}
                    </Card>

                    <StrategicVisionSection />

                    <PillarsSection />
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <div className={SECTION_STACK}>
                <Card
                    padding={'xl'}
                    borderRadius={'2xl'}
                    dataTestId={'company-values-section'}
                >
                    <div className={'w-full'}>
                        <div
                            className={
                                'mb-8 flex flex-col items-center gap-5 sm:flex-row sm:items-start sm:gap-3'
                            }
                        >
                            <div
                                className={
                                    'flex w-fit flex-shrink-0 items-center justify-center !rounded-lg bg-gray-100 p-4 shadow-sm'
                                }
                            >
                                <Icon
                                    name={'support-heart'}
                                    size={35}
                                    colorClassName={'text-teal-400'}
                                    strokeWidth={2}
                                />
                            </div>

                            <div
                                className={
                                    'flex flex-col items-center gap-1.5 text-center sm:!items-start sm:!text-left'
                                }
                            >
                                <h2 className={VISION_SECTION_TITLE}>{t('visionValues.title')}</h2>

                                <p className={'text-base font-medium text-gray-500'}>
                                    {t('visionValues.description')}
                                </p>
                            </div>
                        </div>

                        {companyValues.length === 0 ? (
                            <div className={'py-12 text-center'}>
                                <p className={'text-gray-500'}>{t('visionValues.noValues')}</p>
                            </div>
                        ) : (
                            <div className={'responsive-grid-1-2-3 gap-6 md:!gap-10'}>
                                {companyValues.map((value) => (
                                    <ValueCard
                                        key={value.id}
                                        value={value}
                                        onEdit={() => handleEditValue(value)}
                                    />
                                ))}
                            </div>
                        )}

                        <div className={'mt-8 flex justify-start'}>
                            <span
                                title={
                                    maxValuesReached
                                        ? t('visionValues.maxValuesReached')
                                        : undefined
                                }
                                className={'inline-block'}
                            >
                                <ButtonGradientPrimary
                                    onClick={handleAddValue}
                                    disabled={maxValuesReached}
                                    size={'md'}
                                    leftIcon={
                                        <Icon
                                            name={'essential-add'}
                                            size={'md'}
                                            strokeWidth={3}
                                            color={Colors.WHITE}
                                        />
                                    }
                                    dataTestId={'add-value-button'}
                                >
                                    {t('visionValues.addValue')}
                                </ButtonGradientPrimary>
                            </span>
                        </div>
                    </div>
                </Card>

                <StrategicVisionSection />

                <PillarsSection />
            </div>

            <AddEditValueModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveValue}
                value={editingValue}
                existingValues={companyValues}
            />
        </PageContainer>
    );
}
